import { Product } from "../../common/models/product";
import {
  AREA_MM2_PER_UNIT,
  canonicalizePriceUnit,
  convertDimensionUnits,
  convertWeightUnits,
  isAreaUnit,
  isBarWeightRemnant,
  isLengthUnit,
  isUnitUnit,
  isWeightUnit,
} from "./helpers";
import {
  calculateMaterialVolume,
  crossSectionAreaMm2,
} from "./material-volume-helpers";

// Standard length (in mm) needed to convert a per-unit / per-weight price into
// a per-length price (and vice versa). Returns null when the product has no
// usable standard length — e.g. per-unit-priced scraped sheet variants that
// carry no standardDimensions — so callers bail out instead of dereferencing
// `standardDimensions.length` and crashing.
const standardLengthHiMm = (product: Product): number | null => {
  const value = product.standardDimensions?.length?.hi?.value;
  return typeof value === "number" && value > 0 ? value : null;
};

/**
 * Face area (mm²) of one sheet/plate piece: width × length. Null when either
 * dimension is missing so area pricing is only offered for real sheet
 * geometry. standardDimensions are stored in mm. Mirrors
 * functions/src/pricing-diagnostics.ts.
 */
const sheetAreaMm2 = (product: Product): number | null => {
  const sd = product.standardDimensions;
  const length = sd?.length?.hi?.value;
  const width = sd?.width?.hi?.value;
  return typeof length === "number" &&
    length > 0 &&
    typeof width === "number" &&
    width > 0
    ? length * width
    : null;
};

// Buyer-facing label for a canonical unit token. The generic "unit" token is
// shown as "piece" (we sell discrete pieces, not abstract "units");
// area tokens get their conventional spelling.
export const unitDisplayLabel = (unit: string | undefined): string => {
  if (unit === "unit") return "piece";
  if (unit === "sqft") return "sq ft";
  if (unit === "sqin") return "sq in";
  if (unit === "sqm") return "m²";
  return unit || "";
};

// Inches per length unit, for cut-tier math (tier lengths are stored in inches).
const INCHES_PER_UNIT: Record<string, number> = {
  in: 1,
  inch: 1,
  inches: 1,
  ft: 12,
  foot: 12,
  feet: 12,
  mm: 1 / 25.4,
  cm: 1 / 2.54,
  m: 39.3700787,
};

// Beyond this (2,000 ft) the DP table gets silly; linear pricing is fine there.
const MAX_TIER_TARGET_INCHES = 24000;

/**
 * Cheapest way to buy `quantity` of length from a vendor's stock cut tiers
 * (Product.lengthPriceTiers). Vendors like OnlineMetals sell the same bar as
 * 12"/24"/36"… cuts, each cheaper per foot, so pricing every foot at the 1-ft
 * rate overcharges long orders. Unbounded covering DP at 1-inch granularity —
 * minimum cost over cut combinations covering at least the requested length.
 * Null when tiers don't apply; callers fall back to the linear conversion.
 * Mirrors functions/src/order-total.ts so the display total matches the
 * authoritative order total.
 */
export const cheapestTierTotal = (
  tiers: Product["lengthPriceTiers"],
  quantityValue: number,
  quantityUnit: string,
): number | null => {
  if (!tiers || tiers.length < 2) return null;
  const unitFactor = INCHES_PER_UNIT[(quantityUnit || "").trim().toLowerCase()];
  if (!unitFactor || !(quantityValue > 0)) return null;
  const usable = tiers
    .map((tier) => ({
      lengthIn: Math.round(
        (tier?.length?.value ?? 0) *
          (INCHES_PER_UNIT[(tier?.length?.unit || "in").toLowerCase()] ?? 0),
      ),
      price: tier?.price?.value ?? 0,
    }))
    .filter((tier) => tier.lengthIn > 0 && tier.price > 0);
  if (usable.length < 2) return null;
  const targetIn = Math.ceil(quantityValue * unitFactor);
  if (targetIn <= 0 || targetIn > MAX_TIER_TARGET_INCHES) return null;
  const dp = new Array<number>(targetIn + 1).fill(Infinity);
  dp[0] = 0;
  for (let i = 1; i <= targetIn; i += 1) {
    for (const tier of usable) {
      const prev = i > tier.lengthIn ? i - tier.lengthIn : 0;
      const cost = dp[prev] + tier.price;
      if (cost < dp[i]) dp[i] = cost;
    }
  }
  return Number.isFinite(dp[targetIn])
    ? Math.round(dp[targetIn] * 100) / 100
    : null;
};

const weightAndLength = (targetPriceUnit: string): [string, string] =>
  targetPriceUnit === "kg" ? ["kg", "m"] : ["lb", "ft"];

const addIfNew = (
  prices: { value: number; unit: string }[],
  next: { value: number; unit: string } | null,
) => {
  if (!next?.unit) return;
  if (prices.some((price) => price.unit === next.unit)) return;
  prices.push(next);
};

// Vendor unit first (so "$90 each" stays per piece), then the search weight
// unit. Per-foot is only added when the vendor did not price by the piece —
// a 12 in plate at $90/piece is $90/ft mathematically, which looks like we
// changed their unit. Shared by the product detail and the search results so
// both screens name the same prices.
export const pricesForDisplay = (
  product: Product,
  targetPriceUnit: string,
): { value: number; unit: string }[] => {
  const [weightUnit, lengthUnit] = weightAndLength(targetPriceUnit);
  const storedUnit = canonicalizePriceUnit(product.price?.unit);
  const prices: { value: number; unit: string }[] = [];

  if (storedUnit) {
    addIfNew(prices, convertPriceValueAndUnit(product, storedUnit));
  }
  addIfNew(prices, convertPriceValueAndUnit(product, weightUnit));
  if (!isUnitUnit(storedUnit)) {
    addIfNew(prices, convertPriceValueAndUnit(product, lengthUnit));
  }

  if (prices.length) {
    return prices;
  }
  const fallback = convertPriceValueAndUnit(product, targetPriceUnit);
  return fallback ? [fallback] : [];
};

export const formatPriceValueString = ({
  value,
  currency,
}: {
  value: number;
  currency: string;
}) => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  });

  return formatter.format(value);
};

/**
 * Sort key for the price tiebreaker, mirroring the backend's
 * `priceValueForSort` exactly: the raw numeric price, or +Infinity when the
 * item has no purchasable price (missing, zero, or negative). The backend
 * intentionally compares raw values across units; converting to a common unit
 * here made per-unit-priced items unrankable (no weight data -> Infinity) and
 * reordered the server's ranking.
 */
export const priceValueForSort = (product: Product): number => {
  const value = product.price?.value;
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : Number.POSITIVE_INFINITY;
};

export const formatPriceComponents = (
  product: Product,
  targetPerUnit?: string,
) => {
  if (product.price === undefined || product.price === null) return null;
  // A $0 (or negative) price is not a price — render "Inquire for pricing"
  // rather than "$0.00 per unit".
  if (!(typeof product.price.value === "number" && product.price.value > 0)) {
    return null;
  }

  let price: { value: number; unit: string } = product.price;
  if (targetPerUnit) {
    const convertedPrice = convertPriceValueAndUnit(product, targetPerUnit);
    if (convertedPrice) {
      price = convertedPrice;
    }
  }

  return {
    value: formatPriceValueString({
      value: price.value,
      currency: product.price.currency,
    }),
    unit: price.unit,
  };
};

export const formatPrice = (
  product: Product,
  targetPerUnit?: string,
): string => {
  const formatted = formatPriceComponents(product, targetPerUnit);
  if (!formatted) return "-";

  const { value, unit } = formatted;
  return `${value} per ${unit}`;
};

export const convertPriceValueAndUnit = (product: Product, toUnit: string) => {
  if (!product.price || !product.price.value || !product.price.unit) {
    return null;
  }

  // Canonicalize units so non-standard spellings ("lbs", "feet", etc.) convert
  // correctly instead of falling through to null.
  const normalizedToUnit = canonicalizePriceUnit(toUnit);
  const normalizedPrice = {
    ...product.price,
    unit: canonicalizePriceUnit(product.price.unit),
  };
  const normalizedProduct: Product = {
    ...product,
    price: normalizedPrice,
  };

  if (normalizedPrice.unit === normalizedToUnit) {
    return normalizedPrice;
  }

  if (isWeightUnit(normalizedToUnit)) {
    return convertPriceValueAndUnitToPerWeight(normalizedProduct, normalizedToUnit);
  }

  if (isLengthUnit(normalizedToUnit)) {
    return convertPriceValueAndUnitToPerLength(normalizedProduct, normalizedToUnit);
  }

  if (isUnitUnit(normalizedToUnit)) {
    return convertPriceValueAndUnitToPerUnit(normalizedProduct, normalizedToUnit);
  }

  if (isAreaUnit(normalizedToUnit)) {
    return convertPriceValueAndUnitToPerArea(normalizedProduct, normalizedToUnit);
  }

  return null;
};

/**
 * Price per area unit ($/sqft, $/sqm, ...) for sheet/plate goods. A per-piece
 * price spreads over the sheet's face area (width × length); a per-weight
 * price converts through areal weight (thickness × density). This is what lets
 * a quantity like "10 SF" be priced against a sheet sold by the piece.
 * Mirrors functions/src/pricing-diagnostics.ts.
 */
const convertPriceValueAndUnitToPerArea = (
  product: Product,
  toUnit: string,
): { value: number; unit: string } | null => {
  if (!product.price || !product.price.value || !product.price.unit) {
    return null;
  }
  const mm2PerToUnit = AREA_MM2_PER_UNIT[toUnit];
  if (!mm2PerToUnit) {
    return null;
  }

  if (isAreaUnit(product.price.unit)) {
    const mm2PerFromUnit = AREA_MM2_PER_UNIT[product.price.unit];
    return {
      value: (product.price.value / mm2PerFromUnit) * mm2PerToUnit,
      unit: toUnit,
    };
  }

  if (isUnitUnit(product.price.unit)) {
    const areaMm2 = sheetAreaMm2(product);
    if (!areaMm2) return null;
    return {
      value: (product.price.value / areaMm2) * mm2PerToUnit,
      unit: toUnit,
    };
  }

  if (isWeightUnit(product.price.unit) && product.material?.density) {
    const thicknessMm = product.standardDimensions?.thickness?.hi?.value;
    if (!(typeof thicknessMm === "number" && thicknessMm > 0)) return null;
    const density = product.material.density; // kg/m³
    const kgPerMm2 = (thicknessMm * density) / 1e9;
    const pricePerKg = convertWeightUnits(
      "kg",
      product.price.unit,
      product.price.value,
    );
    return {
      value: pricePerKg * kgPerMm2 * mm2PerToUnit,
      unit: toUnit,
    };
  }

  return null;
};

const convertPriceValueAndUnitToPerWeight = (
  product: Product,
  toUnit: string,
) => {
  if (!product.price || !product.price.value || !product.price.unit) {
    return null;
  }
  if (!isWeightUnit(toUnit)) {
    throw new Error(`Cannot convert price to weight unit: ${toUnit}`);
  }

  if (isWeightUnit(product.price.unit)) {
    return {
      value: convertWeightUnits(
        // toUnit and fromUnit are opposite here because we are converting
        // the price "per unit" to the new "per unit" price.
        toUnit,
        product.price.unit,
        product.price.value,
      ),
      unit: toUnit,
    };
  }

  if (!product.material || !product.material.density) {
    return null; // cannot convert price without material density
  }

  if (isLengthUnit(product.price.unit)) {
    // $/ft → $/lb from cross-section × density. Stock length not required.
    const crossSectionArea = crossSectionAreaMm2(product); // mm²
    if (!crossSectionArea) return null;
    const priceUnitLengthInMm = convertDimensionUnits(
      product.price.unit,
      "mm",
      1,
    );
    const priceUnitLengthVolume = crossSectionArea * priceUnitLengthInMm; // mm³
    if (!priceUnitLengthVolume) return null;

    const density = product.material.density; // kg/m³
    const weightPerUnitLength = (priceUnitLengthVolume * density) / 1e9;
    return {
      value: convertWeightUnits(
        toUnit,
        "kg",
        product.price.value / weightPerUnitLength,
      ),
      unit: toUnit,
    };
  } else if (isUnitUnit(product.price.unit)) {
    // If the price is given in per-unit, we need to calculate the weight per unit
    const volume = calculateMaterialVolume(product);
    if (!volume) return null; // cannot calculate weight per unit
    const density = product.material.density; // in kg/m^3
    const weightPerUnit = (volume * density) / 1e9; // convert mm^3 to m^3
    return {
      value: convertWeightUnits(
        // toUnit and fromUnit are opposite here because we are converting
        // the price "per unit" to the new "per unit" price.
        toUnit,
        "kg",
        product.price.value / weightPerUnit, // convert price per unit to price per unit weight
      ),
      unit: toUnit,
    };
  } else if (isAreaUnit(product.price.unit)) {
    // $/sqft → $/lb via areal weight (thickness × density).
    const thicknessMm = product.standardDimensions?.thickness?.hi?.value;
    if (!(typeof thicknessMm === "number" && thicknessMm > 0)) return null;
    const mm2PerPriceUnit = AREA_MM2_PER_UNIT[product.price.unit];
    const density = product.material.density; // kg/m³
    const kgPerPriceUnit = (mm2PerPriceUnit * thicknessMm * density) / 1e9;
    if (!(kgPerPriceUnit > 0)) return null;
    return {
      value: convertWeightUnits(
        toUnit,
        "kg",
        product.price.value / kgPerPriceUnit,
      ),
      unit: toUnit,
    };
  }
  return null; // cannot convert price to weight unit
};

const convertPriceValueAndUnitToPerLength = (
  product: Product,
  toUnit: string,
): { value: number; unit: string } | null => {
  if (!product.price || !product.price.value || !product.price.unit) {
    return null;
  }
  if (!isLengthUnit(toUnit)) {
    throw new Error(`Cannot convert price to length unit: ${toUnit}`);
  }

  if (isLengthUnit(product.price.unit)) {
    return {
      value: convertDimensionUnits(
        // toUnit and fromUnit are opposite here because we are converting
        // the price "per unit" to the new "per unit" price.
        toUnit,
        product.price.unit,
        product.price.value,
      ),
      unit: toUnit,
    };
  }

  if (isWeightUnit(product.price.unit) && product.material?.density) {
    // $/lb → $/ft. The buyer's requested feet is applied later as quantity;
    // here we only need lb/ft from diameter × density. Random-length listings
    // (diameter only, no stock length) are therefore priceable by the foot.
    const crossSectionArea = crossSectionAreaMm2(product); // mm²
    if (!crossSectionArea) return null;
    const density = product.material.density; // kg/m³
    const kgPerMeter = (crossSectionArea / 1e6) * density;
    const pricePerKg = convertWeightUnits(
      "kg",
      product.price.unit,
      product.price.value,
    );
    const pricePerMeter = pricePerKg * kgPerMeter;
    return {
      value: convertDimensionUnits(toUnit, "m", pricePerMeter),
      unit: toUnit,
    };
  }

  if (isUnitUnit(product.price.unit)) {
    // If the price is given in per-unit, we need to calculate the length per unit

    // e.g. if the price is given in $/bar, we need to calculate the length (e.g. m) per bar.

    // convert price per unit to price per mm (standard length is in mm)
    const lengthHiMm = standardLengthHiMm(product);
    if (!lengthHiMm) return null; // no standard length -> can't express per-length
    const pricePerMm = product.price.value / lengthHiMm;
    return {
      value: convertDimensionUnits(
        // toUnit and fromUnit are opposite here because we are converting
        // the price "per unit" to the new "per unit" price.
        toUnit,
        "mm",
        pricePerMm,
      ),
      unit: toUnit,
    };
  }

  return null; // cannot convert price to length unit
};

const convertPriceValueAndUnitToPerUnit = (
  product: Product,
  toUnit: string,
): { value: number; unit: string } | null => {
  if (!product.price || !product.price.value || !product.price.unit) {
    return null;
  }
  if (!isUnitUnit(toUnit)) {
    throw new Error(`Cannot convert price to unit unit: ${toUnit}`);
  }

  if (isUnitUnit(product.price.unit)) {
    return {
      value: product.price.value,
      unit: toUnit,
    };
  }

  if (isWeightUnit(product.price.unit) && product.material?.density) {
    // Weight-stocked bar remnants: price the piece from actual on-hand weight,
    // not nominal mill-bar geometry (which can imply thousands of lbs per piece).
    if (
      isBarWeightRemnant(product) &&
      product.quantity?.value &&
      isWeightUnit(product.quantity.unit)
    ) {
      const weightInPriceUnit = convertWeightUnits(
        product.quantity.unit,
        product.price.unit,
        product.quantity.value,
      );
      return {
        value: product.price.value * weightInPriceUnit,
        unit: toUnit,
      };
    }

    // If the price is given in per-weight, we need to calculate the weight per unit

    // e.g. if the price is given in $/kg, we need to calculate the kg per unit (e.g. bar).
    // then we can convert the price to $/bar (or whatever the toUnit is) by simply multiplying.

    const volume = calculateMaterialVolume(product); // in mm^3
    if (!volume) return null; // cannot calculate unit per weight
    const density = product.material.density; // in kg/m^3
    const kgPerUnit = (volume * density) / 1e9; // convert mm^3 to m^3
    const weightPerUnit = convertWeightUnits(
      // converting from kg to the unit specified in the product price
      "kg",
      product.price.unit,
      kgPerUnit,
    );
    return {
      value: product.price.value * weightPerUnit,
      unit: toUnit,
    };
  }

  if (isLengthUnit(product.price.unit)) {
    // If the price is given in per-length, we need to calculate the length per unit.

    // e.g. if the price is given in $/m, we need to calculate the m per unit (e.g. bar).
    // then we can convert the price to $/bar (or whatever the toUnit is) by simply multiplying.

    const lengthHiMm = standardLengthHiMm(product);
    if (!lengthHiMm) return null; // no standard length -> can't express per-unit
    const lengthInPriceUnit = convertDimensionUnits(
      // converting from mm to the unit specified in the product price
      "mm",
      product.price.unit,
      lengthHiMm,
    );
    return {
      value: product.price.value * lengthInPriceUnit, // convert price per m to price per unit
      unit: toUnit,
    };
  }

  if (isAreaUnit(product.price.unit)) {
    // $/sqft → $/piece: the piece covers its face area (width × length).
    const areaMm2 = sheetAreaMm2(product);
    if (!areaMm2) return null;
    const mm2PerPriceUnit = AREA_MM2_PER_UNIT[product.price.unit];
    return {
      value: (product.price.value / mm2PerPriceUnit) * areaMm2,
      unit: toUnit,
    };
  }

  return null; // cannot convert price to unit unit
};
